/**
 * @fileoverview "Have a gift code?" on the subscribe page (Profile → Subscription): a box
 * to type a gift code into and redeem it for the signed-in gardener, the
 * same as the gift email's link does (/gift/redeem). It says what came of
 * it: Grow More until a day, a year added to their subscription, or why not
 * (used, switched off, unknown).
 *
 * Where: the bottom of the subscribe page (SubscribePage), the only caller.
 * API: POST /gifts/redeem { code } (redeemGift); then the billing status is
 * read again (awaited, so the page above shows the new state with the
 * message).
 * Words (en/es): gift.box.title, .redeem, .give, .giveLink;
 * gift.redeem.codeLabel and the gift.redeem.* outcomes. A failure on our
 * side (no refusal, or GIFT_FAILED's 502; a 500, no connection; D31,
 * lib/serverFailure) says the standard sentence, reported to Sentry, with the
 * support address as a link, in place of gift.redeem.failed's "Please try
 * again in a moment" (lib/errorMessage); GIFT_UNAVAILABLE keeps its words.
 *
 * @module GiftCodeBox
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Gift } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { cn } from '@/lib/utils';
import { giftRefusalOf, redeemGift } from '@/shared/api/gifts';
import { BILLING_QUERY_KEY } from '@/features/billing/lib/useBilling';
import { forgetGiftCode, giftDay, tidyGiftCode } from '../lib/giftCode';

/**
 * The gift code box.
 *
 * @param props.className - Extra classes for the box.
 */
export const GiftCodeBox: React.FC<{ className?: string }> = ({ className }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const queryClient = useQueryClient();
  const [code, setCode] = React.useState('');
  const [working, setWorking] = React.useState(false);
  const [said, setSaid] = React.useState<{ ok: boolean; words: string } | null>(null);

  /**
   * Redeems the code typed.
   *
   * @param event - The form's submit.
   */
  const redeem = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const tidy = tidyGiftCode(code);
    if (!tidy) return;
    setWorking(true);
    setSaid(null);
    try {
      const result = await redeemGift(tidy);
      forgetGiftCode();
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
      const date = giftDay(result.endsAt, language);
      setSaid({ ok: true, words: result.how === 'subscription' ? t('gift.redeem.doneSubscription', { date }) : t('gift.redeem.done', { date }) });
      setCode('');
    } catch (error) {
      const refusal = giftRefusalOf(error);
      // No refusal, or GIFT_FAILED (a 502): on our side, said by errorMessage.
      const words = !refusal || refusal.code === 'GIFT_FAILED' ? errorMessage(error, t('gift.redeem.failed'))
        : refusal.code === 'GIFT_USED' && refusal.mine ? t('gift.redeem.alreadyYours')
          : refusal.code === 'GIFT_USED' ? t('gift.redeem.used')
            : refusal.code === 'GIFT_VOID' ? t('gift.redeem.void')
              : refusal.code === 'GIFT_UNKNOWN' ? t('gift.redeem.unknown')
                : refusal.code === 'GIFT_TOO_FAR' ? t('gift.redeem.tooFar')
                  : refusal.code === 'GIFT_UNAVAILABLE' ? t('gift.redeem.unavailable') : t('gift.redeem.failed');
      setSaid({ ok: false, words });
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className={cn('rounded-sm border bg-card p-4', className)}>
      <p className="flex items-center gap-2 font-medium">
        <Gift className="h-4 w-4 text-primary" aria-hidden="true" />
        {t('gift.box.title')}
      </p>
      <form className="mt-3 flex flex-wrap gap-2" onSubmit={(event) => { void redeem(event); }}>
        <label htmlFor="gift-code-box" className="sr-only">{t('gift.redeem.codeLabel')}</label>
        <Input
          id="gift-code-box"
          className="w-56 font-mono uppercase tracking-wider"
          placeholder="GROW-XXXX-XXXX"
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button type="submit" variant="outline" disabled={working || !code.trim()}>
          {working ? <Spinner size={14} /> : null}
          <span className={working ? 'ml-2' : ''}>{t('gift.box.redeem')}</span>
        </Button>
      </form>
      {said && (
        <p role={said.ok ? 'status' : 'alert'} className={cn('mt-2 text-sm', said.ok ? 'text-foreground' : 'text-destructive')}>
          <FailureText text={said.words} />
        </p>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        {t('gift.box.give')}{' '}
        <a href="/gift" className="text-primary underline underline-offset-2">{t('gift.box.giveLink')}</a>
      </p>
    </div>
  );
};

export default GiftCodeBox;
